/**
 * Currency Hook
 * Provides currency conversion and formatting utilities
 */

import { useState, useEffect, useCallback } from 'react';
import { analyticsService, ExchangeRates } from '../services/analytics.service';

export type Currency = 'HBAR' | 'USD' | 'INR';

interface CurrencyValue {
  hbar: number;
  usd: number;
  inr: number;
}

interface UseCurrencyReturn {
  selectedCurrency: Currency;
  setSelectedCurrency: (currency: Currency) => void;
  exchangeRates: ExchangeRates | null;
  loading: boolean;
  error: string | null;
  formatAmount: (amount: number, currency?: Currency) => string;
  convertAmount: (amount: number, fromCurrency: Currency, toCurrency: Currency) => number;
  convertHBARToAll: (hbarAmount: number) => CurrencyValue;
  refreshRates: () => Promise<void>;
}

export const useCurrency = (defaultCurrency: Currency = 'HBAR'): UseCurrencyReturn => {
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>(defaultCurrency);
  const [exchangeRates, setExchangeRates] = useState<ExchangeRates | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchExchangeRates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const rates = await analyticsService.getCurrentExchangeRates();
      setExchangeRates(rates);
    } catch (err) {
      setError('Failed to fetch exchange rates');
      console.error('Error fetching exchange rates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchExchangeRates();
  }, [fetchExchangeRates]);

  const formatAmount = useCallback((amount: number, currency: Currency = selectedCurrency): string => {
    const symbols = {
      HBAR: 'ℏ',
      USD: '$',
      INR: '₹'
    };

    const decimals = {
      HBAR: 8,
      USD: 4,
      INR: 2
    };

    if (amount === 0) {
      return `${symbols[currency]}0`;
    }

    if (amount < 0.01 && currency === 'HBAR') {
      return `${symbols[currency]}${amount.toFixed(decimals[currency])}`;
    }

    if (amount < 0.01) {
      return `${symbols[currency]}${amount.toFixed(4)}`;
    }

    if (amount < 1) {
      return `${symbols[currency]}${amount.toFixed(4)}`;
    }

    if (amount < 1000) {
      return `${symbols[currency]}${amount.toFixed(decimals[currency])}`;
    }

    // Format large numbers with commas
    const formatted = amount.toLocaleString('en-US', {
      minimumFractionDigits: currency === 'INR' ? 2 : currency === 'USD' ? 4 : 8,
      maximumFractionDigits: currency === 'INR' ? 2 : currency === 'USD' ? 4 : 8
    });

    return `${symbols[currency]}${formatted}`;
  }, [selectedCurrency]);

  const convertAmount = useCallback((
    amount: number, 
    fromCurrency: Currency, 
    toCurrency: Currency
  ): number => {
    if (!exchangeRates || fromCurrency === toCurrency) {
      return amount;
    }

    // Convert to USD first if needed, then to target currency
    let usdAmount = amount;
    
    if (fromCurrency === 'HBAR') {
      usdAmount = amount * exchangeRates.hbarToUsd;
    } else if (fromCurrency === 'INR') {
      usdAmount = amount / exchangeRates.usdToInr;
    }

    // Convert from USD to target currency
    if (toCurrency === 'USD') {
      return usdAmount;
    } else if (toCurrency === 'HBAR') {
      return usdAmount / exchangeRates.hbarToUsd;
    } else if (toCurrency === 'INR') {
      return usdAmount * exchangeRates.usdToInr;
    }

    return amount;
  }, [exchangeRates]);

  const convertHBARToAll = useCallback((hbarAmount: number): CurrencyValue => {
    if (!exchangeRates) {
      return { hbar: hbarAmount, usd: 0, inr: 0 };
    }

    return {
      hbar: hbarAmount,
      usd: hbarAmount * exchangeRates.hbarToUsd,
      inr: hbarAmount * exchangeRates.hbarToInr
    };
  }, [exchangeRates]);

  const refreshRates = useCallback(async () => {
    await fetchExchangeRates();
  }, [fetchExchangeRates]);

  return {
    selectedCurrency,
    setSelectedCurrency,
    exchangeRates,
    loading,
    error,
    formatAmount,
    convertAmount,
    convertHBARToAll,
    refreshRates
  };
};

export default useCurrency;